import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add, Edit, Visibility, Delete, ShoppingCart, FilterList, Refresh, CheckCircle, Cancel, Close, Print, WhatsApp,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box,
  Card, CardContent, Grid, TextField, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Fade, CircularProgress
} from '@mui/material';
import SaleModal from './SaleModal';
// import InvoicePDFViewer from './InvoicePDFViewer';
import { getCompanyConfigFromAPI } from './CompanyConfig';
import Pagination from '../../common/Pagination';
import './SaleIndex.css';

const SaleIndex = () => {
  // Session validation
  useSessionValidation();
  
  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();

  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [companyData, setCompanyData] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mobile and UI states
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mounted, setMounted] = useState(false);

  // Statistics states
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    verified: 0,
    pending: 0
  });

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());
  
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [saleDetails, setSaleDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // PDF Viewer modal
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedSaleForPDF, setSelectedSaleForPDF] = useState(null);
  const [selectedSaleDetailsForPDF, setSelectedSaleDetailsForPDF] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
      fetchMaxBillNo();
    }
  }, [companyId, token, fromDate, toDate]);

  // Calculate statistics
  const calculateStatistics = (salesData) => {
    const stats = {
      total: salesData.length,
      active: salesData.filter(s => s.IsActive).length,
      verified: 0,
      pending: salesData.filter(s => s.BalanceAmount > 0).length
    };
    setStatistics(stats);
  };

  // Fetch functions
  const fetchSales = async () => {
    try {
      const activeResponse = await fetchData('SaleApi', `/SalesList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));
      
      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('SaleApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive sales:', inactiveError);
      }
      
      const allSales = [...activeData, ...inactiveData];
      setSales(allSales);
      calculateStatistics(allSales);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
      calculateStatistics([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetchData('CustomerApi', `/${companyId}`, token);
      setCustomers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetchData('BankApi', `/${companyId}`, token);
      setBanks(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const fetchCompanyData = async () => {
    try {
      console.log('Fetching company data from API...', { companyId, branchId });
      const apiCompanyData = await getCompanyConfigFromAPI(companyId, branchId, token);
      setCompanyData(apiCompanyData);
      console.log('Company data fetched successfully:', apiCompanyData);
    } catch (error) {
      console.error('Failed to fetch company data from API:', error);
      setCompanyData(null);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const invoiceTypeParam = `/${0}`;
      const response = await fetchData('SaleApi', `/MaxBillno/${companyId}/${branchId || 1}${invoiceTypeParam}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      
      console.log('Max Bill Data:', { billNumber, billPrefix, finYear, rawData: data });
      
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchBranchData = async () => {
    try {
      const response = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      console.log('Branch data response:', response);
      const branchData = response.response_data || response || {};
      const isGSTEnabled = branchData.IsGST !== undefined ? branchData.IsGST : true;
      return isGSTEnabled;
    } catch (error) {
      console.error('Error fetching branch data:', error);
      return true; // Default to true if fetch fails
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Loading sales...');
    try {
      await Promise.allSettled([
        fetchSales(), 
        fetchCustomers(), 
        fetchItems(), 
        fetchBanks(),
        fetchCompanyData()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleSale = async (saleId) => {
    try {
      const response = await fetchData('SaleApi', `/${saleId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single sale:', error);
      return null;
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSingleSale(itemId);

      if (freshData) {
        const saleData = freshData.Sale || freshData;
        const details = freshData.SaleDetails || [];
        
        setSelectedItem(saleData);
        setFormData({
          Id: saleData.Id,
          CompanyId: saleData.CompanyId || companyId,
          BranchId: saleData.BranchId || branchId,
          CustomerId: saleData.CustomerId,
          CustomerName: saleData.CustomerName || '',
          ContactNumber1: saleData.ContactNumber1 || '',
          ContactNumber2: saleData.ContactNumber2 || '',
          AddressLine1: saleData.AddressLine1 || '',
          AddressLine2: saleData.AddressLine2 || '',
          Location: saleData.Location || '',
          PinCode: saleData.PinCode || '',
          BillNumber: saleData.BillNumber,
          FinYear: saleData.FinYear || '',
          BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          Amount: saleData.Amount || 0,
          DiscountAmount: saleData.DiscountAmount || 0,
          NetAmount: saleData.NetAmount || 0,
          CGSTAmount: saleData.CGSTAmount || 0,
          SGSTAmount: saleData.SGSTAmount || 0,
          IGSTAmount: saleData.IGSTAmount || 0,
          RoundOff: saleData.RoundOff || 0,
          TransportingCharge: saleData.TransportingCharge || 0,
          TotalAmount: saleData.TotalAmount || 0,
          AdvanceAmount: saleData.AdvanceAmount || 0,
          PaidAmount: saleData.PaidAmount || 0,
          BalanceAmount: saleData.BalanceAmount || 0,
          ModeOfPayment: saleData.ModeOfPayment || 0,
          Remarks: saleData.Remarks || '',
          IsGST: saleData.IsGST ?? true,
          IsActive: saleData.IsActive ?? true,
          CreatedBy: saleData.CreatedBy,
          CreatedDate: saleData.CreatedDate,
          CreatedByName: saleData.CreatedByName,
          ModifiedBy: saleData.ModifiedBy,
          ModifiedDate: saleData.ModifiedDate,
          ModifiedByName: saleData.ModifiedByName
        });
        setSaleDetails(details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy,
          CreatedDate: d.CreatedDate,
          ModifiedBy: d.ModifiedBy,
          ModifiedDate: d.ModifiedDate
        })));
      }
      setModalLoading(false);
    } else {
      setModalLoading(true);
      const maxBillData = await fetchMaxBillNo();
      const branchIsGST = await fetchBranchData();
      
      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        CustomerId: null,
        CustomerName: '',
        ContactNumber1: '',
        ContactNumber2: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',
        BillNumber: maxBillData.billNumber || 1,
        FinYear: maxBillData.finYear || '',
        BillDate: new Date().toISOString().split('T')[0],
        Amount: 0,
        DiscountAmount: 0,
        NetAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        RoundOff: 0,
        TransportingCharge: 0,
        TotalAmount: 0,
        AdvanceAmount: 0,
        PaidAmount: 0,
        BalanceAmount: 0,
        ModeOfPayment: 0,
        Remarks: '',
        IsGST: branchIsGST,
        IsActive: true
      });
      setSaleDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setSaleDetails([]);
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} sale...`);

      const dataToSend = {
        Sale: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || branchId,
          CustomerId: formData.CustomerId || null,
          CustomerName: formData.CustomerName || '',
          ContactNumber1: formData.ContactNumber1 || '',
          ContactNumber2: formData.ContactNumber2 || '',
          AddressLine1: formData.AddressLine1 || '',
          AddressLine2: formData.AddressLine2 || '',
          Location: formData.Location || '',
          PinCode: formData.PinCode || '',
          BillNumber: parseInt(formData.BillNumber) || 0,
          FinYear: formData.FinYear || '',
          BillDate: formData.BillDate || new Date().toISOString().split('T')[0],
          Amount: Math.round((parseFloat(formData.Amount) || 0) * 100) / 100,
          DiscountAmount: Math.round((parseFloat(formData.DiscountAmount) || 0) * 100) / 100,
          NetAmount: Math.round((parseFloat(formData.NetAmount) || 0) * 100) / 100,
          CGSTAmount: Math.round((parseFloat(formData.CGSTAmount) || 0) * 100) / 100,
          SGSTAmount: Math.round((parseFloat(formData.SGSTAmount) || 0) * 100) / 100,
          IGSTAmount: Math.round((parseFloat(formData.IGSTAmount) || 0) * 100) / 100,
          RoundOff: Math.round((parseFloat(formData.RoundOff) || 0) * 100) / 100,
          TransportingCharge: Math.round((parseFloat(formData.TransportingCharge) || 0) * 100) / 100,
          TotalAmount: Math.round((parseFloat(formData.TotalAmount) || 0) * 100) / 100,
          AdvanceAmount: Math.round((parseFloat(formData.AdvanceAmount) || 0) * 100) / 100,
          PaidAmount: Math.round((parseFloat(formData.PaidAmount) || 0) * 100) / 100,
          BalanceAmount: Math.round((parseFloat(formData.BalanceAmount) || 0) * 100) / 100,
          ModeOfPayment: parseInt(formData.ModeOfPayment) || 0,
          Remarks: formData.Remarks || '',
          IsGST: formData.IsGST ?? true,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : formData.CreatedDate,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        SaleDetails: saleDetails.map(detail => ({
          Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
          CompanyId: companyId,
          SaleId: modalAction === 'edit' ? formData.Id : 0,
          ItemId: parseInt(detail.ItemId) || 0,
          HSNCode: detail.HSNCode || '',
          GSTRate: Math.round((parseFloat(detail.GSTRate) || 0) * 100) / 100,
          Quantity: Math.round((parseFloat(detail.Quantity) || 0) * 100) / 100,
          Rate: Math.round((parseFloat(detail.Rate) || 0) * 100) / 100,
          Amount: Math.round((parseFloat(detail.Amount) || 0) * 100) / 100,
          DiscountPercentage: Math.round((parseFloat(detail.DiscountPercentage) || 0) * 100) / 100,
          DiscountAmount: Math.round((parseFloat(detail.DiscountAmount) || 0) * 100) / 100,
          TaxableAmount: Math.round((parseFloat(detail.TaxableAmount) || 0) * 100) / 100,
          CGSTAmount: Math.round((parseFloat(detail.CGSTAmount) || 0) * 100) / 100,
          SGSTAmount: Math.round((parseFloat(detail.SGSTAmount) || 0) * 100) / 100,
          IGSTAmount: Math.round((parseFloat(detail.IGSTAmount) || 0) * 100) / 100,
          TotalAmount: Math.round((parseFloat(detail.TotalAmount) || 0) * 100) / 100,
          IsActive: detail.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : detail.CreatedDate,
          ModifiedBy: modalAction === 'edit' ? userId : null
        }))
      };

      if (modalAction === 'create') {
        await postData('SaleApi', dataToSend, token);
        setSuccess('Sale created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Sale ID is missing');
        await updateData('SaleApi', formData.Id, dataToSend, token);
        setSuccess('Sale updated successfully!');
      }

      handleCloseModal();
      await fetchSales();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} sale. Please try again.`);
      setLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, billNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(billNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('SaleApi', deleteItemId, token);
      setSuccess('Sale deleted successfully!');
      await fetchSales();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete sale.');
    }
    setLoading(false);
  };

  // Activate handlers
  const handleOpenActivateModal = (id, billNo) => {
    setActivateItemId(id);
    setActivateItemNo(billNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  const handleActivate = async () => {
    if (!activateItemId) return;

    setLoading(true);
    handleCloseActivateModal();
    try {
      await activateData('SaleApi', activateItemId, token);
      setSuccess('Sale activated successfully!');
      await fetchSales();
    } catch (error) {
      console.error('Error activating:', error);
      setError('Failed to activate sale.');
    }
    setLoading(false);
  };

  // PDF handlers
  const handleOpenPDFViewer = async (saleId) => {
    setLoading(true);
    try {
      const freshData = await fetchSingleSale(saleId);
      if (freshData) {
        const saleData = freshData.Sale || freshData;
        const details = freshData.SaleDetails || [];
        
        setSelectedSaleForPDF(saleData);
        setSelectedSaleDetailsForPDF(details);
        setPdfViewerOpen(true);
      }
    } catch (error) {
      console.error('Error fetching sale for PDF:', error);
      setError('Failed to load sale data for PDF generation.');
    }
    setLoading(false);
  };

  const handleClosePDFViewer = () => {
    setPdfViewerOpen(false);
    setSelectedSaleForPDF(null);
    setSelectedSaleDetailsForPDF([]);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];
    
    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customers.find(c => c.Id === item.CustomerId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleRefresh = () => fetchAllData();
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(sales);

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        {loading && <LoadingOverlay message={loadingMessage} />}
        
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Roman',
        }}>
          {/* Header Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Roman',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Roman' }}>Sale Management</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Date Filters, Search and Create Button Row */}
          <div style={{ 
            marginBottom: '12px', 
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={handleToDateChange}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>
              
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea' },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              )}

              {hasCreateMasterPermission && (
                <Tooltip title="Create a new sale" arrow>
                  <Button
                    variant="contained"
                    onClick={() => handleOpenModal('create')}
                    className="action-button"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      padding: isMobile ? '0' : '4px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '700',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                      border: 'none',
                      height: '32px',
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap',
                      minWidth: isMobile ? '32px' : '100px',
                      width: 'auto',
                      marginLeft: 'auto'
                    }}
                  >
                    <Add style={{ fontSize: '18px' }} />
                    {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="sale table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    height: '28px'
                  }}>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Bill #
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Customer
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Date
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Total
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Paid
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Balance
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'center',
                      height: '28px'
                    }}>
                      Status
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'center',
                      height: '28px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <TableRow
                        key={item.Id || index}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                          transition: 'background-color 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      >
                        <TableCell style={{
                          padding: '8px',
                          fontSize: '11px',
                          color: '#1f2937',
                          fontWeight: '600'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <ShoppingCart style={{ fontSize: '16px', color: '#667eea' }} />
                            {item.BillNumber || '-'}
                          </div>
                        </TableCell>
                        <TableCell style={{
                          padding: '8px',
                          fontSize: '11px',
                          color: '#374151'
                        }}>
                          {item.CustomerName || customers.find(c => c.Id === item.CustomerId)?.Name || '-'}
                        </TableCell>
                        <TableCell style={{
                          padding: '8px',
                          fontSize: '11px',
                          color: '#6b7280'
                        }}>
                          {formatDate(item.BillDate)}
                        </TableCell>
                        <TableCell style={{
                          padding: '8px',
                          fontSize: '11px',
                          color: '#1f2937',
                          fontWeight: '700',
                          textAlign: 'right'
                        }}>
                          {formatCurrency(item.TotalAmount)}
                        </TableCell>
                        <TableCell style={{
                          padding: '8px',
                          fontSize: '11px',
                          color: '#059669',
                          fontWeight: '700',
                          textAlign: 'right'
                        }}>
                          {formatCurrency(item.PaidAmount)}
                        </TableCell>
                        <TableCell style={{
                          padding: '8px',
                          fontSize: '11px',
                          color: '#dc2626',
                          fontWeight: '700',
                          textAlign: 'right'
                        }}>
                          {formatCurrency(item.BalanceAmount)}
                        </TableCell>
                        <TableCell style={{
                          padding: '8px',
                          textAlign: 'center'
                        }}>
                          <Chip
                            label={item.IsActive ? 'Active' : 'Inactive'}
                            size="small"
                            style={{
                              backgroundColor: item.IsActive ? '#d1fae5' : '#fee2e2',
                              color: item.IsActive ? '#065f46' : '#991b1b',
                              fontWeight: '600',
                              fontSize: '9px',
                              height: '20px',
                              borderRadius: '10px'
                            }}
                          />
                        </TableCell>
                        <TableCell style={{
                          padding: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <Tooltip title="View" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenModal('view', item.Id)}
                                style={{
                                  padding: '4px',
                                  color: '#667eea'
                                }}
                              >
                                <Visibility style={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print Invoice" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPDFViewer(item.Id)}
                                style={{
                                  padding: '4px',
                                  color: '#7c3aed'
                                }}
                              >
                                <Print style={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Send WhatsApp" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const message = `*Sale Invoice*\n\nBill No: ${item.BillNumber}\nCustomer: ${item.CustomerName}\nTotal Amount: ₹${(item.TotalAmount || 0).toLocaleString('en-IN')}\nPaid Amount: ₹${(item.PaidAmount || 0).toLocaleString('en-IN')}\nBalance: ₹${(item.BalanceAmount || 0).toLocaleString('en-IN')}`;
                                  const encodedMessage = encodeURIComponent(message);
                                  const phoneNumber = item.ContactNumber1?.replace(/[^0-9]/g, '');
                                  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
                                  window.open(whatsappUrl, '_blank');
                                }}
                                style={{
                                  padding: '4px',
                                  color: '#25D366'
                                }}
                              >
                                <WhatsApp style={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                            {item.IsActive && hasEditMasterPermission && (
                              <Tooltip title="Edit" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenModal('edit', item.Id)}
                                  style={{
                                    padding: '4px',
                                    color: '#f59e0b'
                                  }}
                                >
                                  <Edit style={{ fontSize: '16px' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {item.IsActive && hasDeleteMasterPermission && (
                              <Tooltip title="Delete" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteModal(item.Id, item.BillNumber)}
                                  style={{
                                    padding: '4px',
                                    color: '#ef4444'
                                  }}
                                >
                                  <Delete style={{ fontSize: '16px' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!item.IsActive && hasEditMasterPermission && (
                              <Tooltip title="Activate" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenActivateModal(item.Id, item.BillNumber)}
                                  style={{
                                    padding: '4px',
                                    color: '#10b981'
                                  }}
                                >
                                  <CheckCircle style={{ fontSize: '16px' }} />
                                </IconButton>
                            </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                        <WarningIcon style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '8px' }} />
                        <Typography style={{ fontSize: '14px', fontWeight: '500' }}>
                          No sales found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          {totalCount > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              totalCount={totalCount}
              startIndex={startIndex}
            />
          )}
        </div>

        {/* Sale Modal */}
        <SaleModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          saleDetails={saleDetails}
          setSaleDetails={setSaleDetails}
          onSave={handleSave}
          customers={customers}
          items={items}
          banks={banks}
          loading={modalLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
          onPrintInvoice={handleOpenPDFViewer}
        />

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Delete style={{ color: '#ef4444' }} />
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete sale <strong>{deleteItemNo}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteModal} style={{ color: '#6b7280' }}>
              Cancel
            </Button>
            <Button onClick={handleDelete} variant="contained" style={{ backgroundColor: '#ef4444', color: 'white' }}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Modal */}
        <Dialog open={activateModalOpen} onClose={handleCloseActivateModal}>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckCircle style={{ color: '#10b981' }} />
            Confirm Activate
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to activate sale <strong>{activateItemNo}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseActivateModal} style={{ color: '#6b7280' }}>
              Cancel
            </Button>
            <Button onClick={handleActivate} variant="contained" style={{ backgroundColor: '#10b981', color: 'white' }}>
              Activate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbar */}
        <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
        <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        {/* PDF Viewer Modal */}
        {/* <InvoicePDFViewer
          open={pdfViewerOpen}
          onClose={handleClosePDFViewer}
          saleData={selectedSaleForPDF}
          saleDetails={selectedSaleDetailsForPDF}
          customerData={null}
          companyData={companyData}
          items={items}
        /> */}
      </div>
    </Fade>
  );
};

export default SaleIndex;
